import {
  FunctionComponent,
  memo,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import { Category } from "@dto/Category";
import { selectDatePickerDateRange } from "@store/datePickerReducer";
import {
  selectMenuItem,
  selectPageNumber,
  selectSelectedCategories,
  selectSelectedExclusiveCategories,
} from "@store/menuItem/menuItemReducer";
import { useAppSelector } from "@store/store";
import { MarkerData } from "./MarkerData";
import { MapContext } from "../MapContext";
import { selectSelectedEntity, selectViewport } from "@store/map/mapReducer";
import { getEntityUrlPath } from "@lib/getEntityUrlPath";
import { useGetMapMarkersQuery } from "@store/services/mapMarker";
import { useNavigate } from "@hooks/useNavigate/useNavigate";
import { selectClient } from "@store/client/clientReducer";
import { Marker } from "../elements/Marker";
import { useWindowSize } from "@hooks/useWindowSize/useWindowSize";
import { useMatch } from "react-router";
import { usePin } from "../elements/Pins/usePin";
import isEmpty from "lodash/isEmpty";

const getCategory = (
  categoryIds: number[],
  categoriesMap: Map<number, Category>,
): Category | undefined => {
  for (let i = 0; i < categoryIds.length; i++) {
    const category = categoriesMap.get(categoryIds[i]);
    if (category) {
      return category;
    }
  }
  return categoriesMap.values()[0];
};

interface EntityMapMarkerProps {
  /**
   * Marker data related to the entity it represents.
   */
  marker: MarkerData;
  /**
   * Marker pressed callback.
   */
  handlePress: (marker: MarkerData) => () => void;
}

/**
 * Marker component for the entity.
 */
const EntityMapMarker: FunctionComponent<EntityMapMarkerProps> = ({
  marker,
  handlePress,
}) => {
  const selectedEntity = useAppSelector(selectSelectedEntity);
  const isSelected = selectedEntity?.entity?.id === marker.entityId;
  const pinType = isSelected ? "tearDrop" : "circle";
  const { pin, width, height, anchorX, anchorY } = usePin(
    pinType,
    marker.icon ?? "",
    marker.categoryColor ?? "",
  );

  return (
    <Marker
      position={marker.position}
      icon={pin}
      name={marker.name}
      height={height}
      width={width}
      anchorX={anchorX}
      anchorY={anchorY}
      isSelected={isSelected}
      onClick={handlePress(marker)}
    />
  );
};

EntityMapMarker.displayName = "EntityMapMarker";

const EntityMarkersBase = () => {
  const datePickerDateRange = useAppSelector(selectDatePickerDateRange);
  const menuItem = useAppSelector(selectMenuItem);
  const viewport = useAppSelector(selectViewport);
  const categories = useAppSelector(selectSelectedCategories);
  const exclusiveCategories = useAppSelector(selectSelectedExclusiveCategories);
  const client = useAppSelector(selectClient);
  const page = useAppSelector(selectPageNumber);
  const markersDataRef = useRef<MarkerData[]>([]);
  const { isMobile } = useWindowSize();
  const isPlanScreen = useMatch("/plan");
  const selectedEntity = useAppSelector(selectSelectedEntity);
  const { data: markersData } = useGetMapMarkersQuery(
    {
      categoryIds: categories.map((category) => category.id),
      exclusiveCategoryIds: exclusiveCategories.map((category) => category.id),
      startDate: datePickerDateRange?.startDate,
      endDate: datePickerDateRange?.endDate,
      entityType: menuItem.entity,
      page: page,
      geographicPaginationEnabled: client.geographicPaginationEnabled,
      bbox: viewport?.bbox,
    },
    {
      // The first page will return all entities for the first `locationPageSizeMultiplier` pages.
      skip:
        page !== 1 && page <= VisitWidget.settings.locationPageSizeMultiplier,
    },
  );

  const categoriesMap = useMemo(() => {
    return categories.reduce((acc, category) => {
      acc.set(category.id, category);
      return acc;
    }, new Map<number, Category>());
  }, [categories]);

  const navigate = useNavigate();
  const { setSelectedMarker } = useContext(MapContext);

  const markers = useMemo(() => {
    if (page > 1 && page <= VisitWidget.settings.locationPageSizeMultiplier) {
      return markersDataRef.current;
    }

    const markersLatest = (
      markersData?.map((x) => {
        const category = getCategory(x.categoryIds, categoriesMap);
        const icon = category?.whiteSvgIconUrl;

        return {
          entityId: x.id,
          entityType: x.type,
          key: `${x.type}-${x.id}`,
          name: x.name,
          icon: icon,
          position: {
            lat: x.location.latitude,
            lng: x.location.longitude,
          },
          thumbnailUrl: x.thumbnailUrl,
          categoryColor: category?.color,
          categoryName: category?.name,
          slug: x.slug,
        } as MarkerData;
      }) ?? []
    ).filter((x) => !isEmpty(x.icon));

    if (page !== 1) {
      const next = Array.from(
        new Map(
          [...markersDataRef.current, ...markersLatest].map((item) => [
            item.key,
            item,
          ]),
        ).values(),
      );
      markersDataRef.current = next;
    } else {
      markersDataRef.current = markersLatest;
    }

    return markersDataRef.current;
  }, [page, markersData, categoriesMap]);

  const handlePress = useCallback(
    (marker: MarkerData) => () => {
      if (!isMobile) {
        navigate(
          getEntityUrlPath(marker.slug, marker.entityId, marker.entityType),
        );
      }
      setSelectedMarker(marker);
    },
    [navigate, setSelectedMarker, isMobile],
  );

  if (
    isPlanScreen ||
    (selectedEntity?.entity &&
      "planItems" in selectedEntity.entity &&
      selectedEntity.entity.planItems.length > 0)
  ) {
    return;
  }

  return (
    <>
      {markers?.map((marker) => (
        <EntityMapMarker
          key={marker.key}
          marker={marker}
          handlePress={handlePress}
        />
      ))}
    </>
  );
};

EntityMarkersBase.displayName = "EntityMarkersBase";

const EntityMarkers = memo(EntityMarkersBase);

EntityMarkers.displayName = "EntityMarkers";

export { EntityMarkers };
